/**
 * CouplesSlideshow — full-bleed background of 5 curated couple photos with
 * Ken-Burns zoom + cross-fade transitions. Used on AuthPage.
 *
 * Timings (matches mobile spec):
 *   - Cross-fade: 700ms cubic ease-in-out, every 5.5s
 *   - Ken-Burns: 12s ease-in-out oscillation (scale 1.00 ↔ 1.08), continuous
 *   - Optional dark scrim overlays for legibility of foreground content
 *
 * Slides are pulled from /public/couple-{1..5}.jpg — verbatim mobile assets.
 * If couple-5.jpg is missing, the array falls back to 4 slides gracefully.
 */
import { useEffect, useState } from 'react';

const SLIDES = [
  { src: '/couple-1.jpg', label: 'Chloe & Henry' },
  { src: '/couple-2.jpg', label: 'Sofía & Diego' },
  { src: '/couple-3.jpg', label: 'Mei & Kenji' },
  { src: '/couple-4.jpg', label: 'Priya & Rohan' },
  { src: '/couple-5.jpg', label: 'Amara & Malik' },
];

const CROSSFADE_MS = 700;
const SLIDE_INTERVAL_MS = 5500;

export default function CouplesSlideshow({ overlay = 'dark', showCaption = true }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % SLIDES.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  const current = SLIDES[idx];

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden" data-testid="couples-slideshow">
      {SLIDES.map((s, i) => (
        <div
          key={s.src}
          className={`absolute inset-0 slideshow-slide ${i === idx ? 'is-active' : ''}`}
          style={{
            backgroundImage: `url(${s.src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transition: `opacity ${CROSSFADE_MS}ms cubic-bezier(0.4,0,0.2,1)`,
            opacity: i === idx ? 1 : 0,
          }}
          aria-hidden={i !== idx}
        />
      ))}

      {overlay === 'dark' && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.75) 100%)',
          }}
        />
      )}

      {/* Caption removed Feb 2026 — user requested names + "Met on Meet to Keep" wording be removed from auth screen. */}
      {false && showCaption && (
        <div className="absolute bottom-6 left-0 right-0 flex justify-center pointer-events-none">
          <p
            className="text-[11px] uppercase tracking-[0.16em] font-semibold text-white/70"
            data-testid="couples-slideshow-caption"
          >
            {current.label}
          </p>
        </div>
      )}

      <style>{`
        @keyframes kenburns {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.08); }
        }
        .slideshow-slide.is-active {
          animation: kenburns 12s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
